import { useEffect, useState } from 'react';
import { Building2, MapPin, Phone, Mail, Lock, Globe, Linkedin, Facebook, Instagram, Twitter, Youtube } from 'lucide-react';
import { LocationInput } from '@/components/location/LocationInput';
import { useAuthStore } from '@/store/authStore';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

export function CompanyContactInfoStep({ data, onChange }: Props) {
  const { user } = useAuthStore();
  const [errors, setErrors] = useState({
    contactPerson: '',
    phone: '',
    businessLocation: ''
  });
  const [touched, setTouched] = useState({
    contactPerson: false,
    phone: false,
    businessLocation: false
  });

  // Auto-fill fields from signup data on mount
  useEffect(() => {
    if (user && (!data.email || !data.phone)) {
      onChange({
        ...data,
        email: data.email || user.email || '',
        phone: data.phone || user.phone || '',
        businessName: data.businessName || user.businessName || ''
      });
    }
  }, [user]);

  const validateField = (field: string, value: any) => {
    let error = '';
    
    switch (field) {
      case 'phone':
        if (!value?.trim()) error = 'Company phone number is required';
        break;
      case 'businessLocation':
        if (!value?.trim()) error = 'Business location is required';
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const updateField = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
    if (touched[field as keyof typeof touched]) {
      validateField(field, value);
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, data[field]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Company Contact Information</h2>
        <p className="text-gray-600">Primary contact details for your company</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building2 className="w-4 h-4 inline mr-2" />
            Company Name
          </label>
          <input
            type="text"
            value={data.businessName || ''}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            disabled
          />
          <p className="text-xs text-gray-500 mt-1">Set during registration</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Person (Optional)
          </label>
          <input
            type="text"
            value={data.contactPerson || ''}
            onChange={(e) => updateField('contactPerson', e.target.value)}
            onBlur={() => handleBlur('contactPerson')}
            placeholder="e.g., John Doe"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">Primary contact person name</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            Company Email *
          </label>
          <div className="relative">
            <input
              type="email"
              value={data.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              disabled
            />
            <Lock className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-2" />
            Company Phone *
          </label>
          <input
            type="tel"
            value={data.phone || ''}
            onChange={(e) => updateField('phone', e.target.value)}
            onBlur={() => handleBlur('phone')}
            placeholder="+353 1 234 5678"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.phone && touched.phone ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.phone && touched.phone && (
            <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <MapPin className="w-4 h-4 inline mr-2" />
          Business Location *
        </label>
        <LocationInput
          value={data.businessLocation || ''}
          onChange={(value) => {
            updateField('businessLocation', value);
            handleBlur('businessLocation');
          }}
          placeholder="Enter your business address"
        />
        {errors.businessLocation && touched.businessLocation && (
          <p className="text-xs text-red-600 mt-1">{errors.businessLocation}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">Full business address</p>
      </div>

      {/* Website & Social Media */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Online Presence (Optional)</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="w-4 h-4 inline mr-2" />
              Company Website
            </label>
            <input
              type="url"
              value={data.website || ''}
              onChange={(e) => updateField('website', e.target.value)}
              placeholder="https://yourcompany.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Linkedin className="w-4 h-4 inline mr-2" />
              LinkedIn
            </label>
            <input
              type="url"
              value={data.socialMedia?.linkedin || ''}
              onChange={(e) => updateField('socialMedia', { ...data.socialMedia, linkedin: e.target.value })}
              placeholder="https://linkedin.com/company/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Facebook className="w-4 h-4 inline mr-2" />
              Facebook
            </label>
            <input
              type="url"
              value={data.socialMedia?.facebook || ''}
              onChange={(e) => updateField('socialMedia', { ...data.socialMedia, facebook: e.target.value })}
              placeholder="https://facebook.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Instagram className="w-4 h-4 inline mr-2" />
              Instagram
            </label>
            <input
              type="url"
              value={data.socialMedia?.instagram || ''}
              onChange={(e) => updateField('socialMedia', { ...data.socialMedia, instagram: e.target.value })}
              placeholder="https://instagram.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Twitter className="w-4 h-4 inline mr-2" />
              Twitter/X
            </label>
            <input
              type="url"
              value={data.socialMedia?.twitter || ''}
              onChange={(e) => updateField('socialMedia', { ...data.socialMedia, twitter: e.target.value })}
              placeholder="https://twitter.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Youtube className="w-4 h-4 inline mr-2" />
              YouTube
            </label>
            <input
              type="url"
              value={data.socialMedia?.youtube || ''}
              onChange={(e) => updateField('socialMedia', { ...data.socialMedia, youtube: e.target.value })}
              placeholder="https://youtube.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
